import React, { useRef, useEffect, useCallback } from 'react';
import { Pressable, NativeSyntheticEvent, NativeScrollEvent } from 'react-native';
import { ScrollView } from '~/components/gluestack/scroll-view';
import { Text } from '~/components/gluestack/text';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { HStack } from '~/components/gluestack/hstack';
import { VStack } from '~/components/gluestack/vstack';

export interface TabItem {
  key: string;
  label: string;
}

interface InlineTabsBarProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tab: string) => void;
  hidden?: boolean;
  className?: string;
  textClassName?: string;
  activeTextClassName?: string;
  variant?: 'underline' | 'buttons';
  // Optional render function for a suffix element (e.g., a badge) next to each tab label.
  // MUST return null/undefined when nothing should be shown to avoid layout gaps.
  // When provided, we will append consistent spacing classes to the returned element
  // without introducing a wrapper that could add unwanted space when the suffix is absent.
  renderSuffix?: (tab: TabItem, isActive: boolean) => React.ReactNode;
  // Shared ref to persist horizontal scroll position across remounts (e.g. sticky headers)
  scrollXRef?: { current: number };
}

const SCROLL_PADDING = 24;

export const InlineTabsBar = ({
  tabs,
  activeTab,
  onTabChange,
  hidden = false,
  className,
  textClassName,
  activeTextClassName,
  variant = 'underline',
  renderSuffix,
  scrollXRef,
}: InlineTabsBarProps) => {
  const scrollViewRef = useRef<any>(null);
  const tabLayouts = useRef<Record<string, { x: number; width: number }>>({});
  const viewWidth = useRef(0);
  const scrollX = useRef(0);

  // Restore persisted scroll position on mount
  useEffect(() => {
    if (scrollXRef && scrollXRef.current > 0) {
      setTimeout(() => scrollViewRef.current?.scrollTo?.({ x: scrollXRef.current, animated: false }), 0);
    }
  }, [scrollXRef]);

  // Scroll active tab into view when it changes
  useEffect(() => {
    const tab = tabLayouts.current[activeTab];
    if (!tab || !viewWidth.current) return;
    const x = scrollX.current;
    const tabEnd = tab.x + tab.width;
    const targetX =
      tab.x < x ? tab.x - SCROLL_PADDING :
      tabEnd > x + viewWidth.current ? tabEnd - viewWidth.current + SCROLL_PADDING :
      null;
    if (targetX != null) {
      setTimeout(() => scrollViewRef.current?.scrollTo?.({ x: Math.max(0, targetX), animated: true }), 50);
    }
  }, [activeTab]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollX.current = e.nativeEvent.contentOffset.x;
      if (scrollXRef) scrollXRef.current = scrollX.current;
    },
    [scrollXRef]
  );

  const onTabLayout = useCallback((key: string, e: { nativeEvent: { layout: { x: number; width: number } } }) => {
    tabLayouts.current[key] = { x: e.nativeEvent.layout.x, width: e.nativeEvent.layout.width };
  }, []);

  if (hidden) {
    return null;
  }

  return (
    <VStack
      className={`${
        variant === 'underline' ? 'border-b border-background-200 dark:border-background-50' : ''
      } ${className ? ` ${className}` : ''}`}>
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerClassName="mx-2"
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onLayout={(e) => { viewWidth.current = e.nativeEvent.layout.width; }}>
        {variant === 'buttons'
          ? tabs.map((tab) => (
              <HStack
                key={tab.key}
                className="items-center px-2 py-2 md:pt-0"
                onLayout={(e) => onTabLayout(tab.key, e)}>
                <ButtonWrapper
                  text={tab.label}
                  size="sm"
                  variant="solid"
                  action={activeTab === tab.key ? 'primary' : 'neutral'}
                  onPress={() => onTabChange(tab.key)}
                  className="px-3"
                  // Avoid wrappers or class mutations; let Button manage centering
                  rightSlot={
                    renderSuffix
                      ? (() => {
                          const node = renderSuffix(tab, activeTab === tab.key);
                          return node || undefined;
                        })()
                      : undefined
                  }
                />
              </HStack>
            ))
          : tabs.map((tab) => (
              <Pressable
                key={tab.key}
                onPress={() => onTabChange(tab.key)}
                onLayout={(e) => onTabLayout(tab.key, e)}
                className="px-3 pt-3">
                <HStack className="items-baseline gap-2">
                  <VStack className="items-center">
                    <Text
                      size="md"
                      className={`pb-1 ${
                        activeTab === tab.key
                          ? `font-extrabold text-typography-800${activeTextClassName ? ` ${activeTextClassName}` : ''}`
                          : `web:hover:text-text-primary-500 font-semibold text-typography-500 dark:text-typography-500 dark:web:hover:text-primary-500${textClassName ? ` ${textClassName}` : ''}`
                      }`}>
                      {tab.label}
                    </Text>
                    {activeTab === tab.key && <HStack className="mt-1 h-1 w-full bg-primary-500" />}
                  </VStack>
                  {/* Render an optional suffix (e.g., a count badge) without introducing
                      extra spacing when it is not present. If the suffix is a valid
                      React element, clone it to append consistent spacing. */}
                  {(() => {
                    if (!renderSuffix) return null;
                    const node = renderSuffix(tab, activeTab === tab.key);
                    if (!node) return null;
                    if (React.isValidElement(node)) {
                      const existingClassName = (node.props as any)?.className ?? '';
                      return React.cloneElement(node as React.ReactElement<any>, {
                        className: `${existingClassName} ml-1`.trim(),
                      });
                    }
                    return <HStack className="ml-1">{node}</HStack>;
                  })()}
                </HStack>
              </Pressable>
            ))}
      </ScrollView>
    </VStack>
  );
};
